import {
  ClipboardEventHandler,
  FocusEventHandler,
  FormEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VirtualElement } from "@popperjs/core";
import { useDebouncedCallback } from "use-debounce";
import { Variable } from "../../models/interactions/Variable";
import { registerPendingTextEdit } from "../../utils/pendingTextEdits";
import {
  VariableTokenIssue,
  VariableTokenOptions,
} from "../variables/VariableTokenUtils";
import { ENABLED_INLINE_FORMATS } from "./RichTextFormattingCommands";
import {
  editorDomToCanonical,
  richTextToEditorHtml,
} from "./RichTextHtmlUtils";
import useRichTextIntellisense from "./useRichTextIntellisense.hook";

interface UseRichTextEditorControllerOptions extends VariableTokenOptions {
  value: string;
  onCommit: (nextValue: string) => void;
  variables: Variable[];
  debounceMs?: number;
  enableVariableIntellisense?: boolean;
  flushOnCleanupKeys?: readonly unknown[];
  /**
   * When set, Enter submits the current canonical value instead of inserting
   * a newline (Shift+Enter still inserts one). Return true to clear the
   * editor after submitting.
   */
  onSubmitEnter?: (currentValue: string) => boolean | void;
}

function getWarningText(issues: VariableTokenIssue[]): string {
  const uniqueIssues = Array.from(new Set(issues.map((issue) => issue.tokenText)));
  if (uniqueIssues.length === 0) return "";
  if (uniqueIssues.length === 1) {
    return `Unknown variable: ${uniqueIssues[0]}`;
  }
  return `Unknown variables: ${uniqueIssues.join(", ")}`;
}

/**
 * Rich text counterpart of useVariableTextareaController: manages an
 * uncontrolled contentEditable div, serializes its DOM to the canonical
 * stored HTML format on input, and commits through the same debounced
 * pending-edit pipeline. The editor DOM is only (re)written from the stored
 * value while the editor is not focused — writing innerHTML on a focused
 * editor would reset the caret.
 */
export default function useRichTextEditorController({
  value,
  onCommit,
  variables,
  debounceMs = 500,
  allowVariables = true,
  checkVariables = true,
  enableVariableIntellisense = true,
  flushOnCleanupKeys = [],
  onSubmitEnter,
}: UseRichTextEditorControllerOptions) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [issues, setIssues] = useState<VariableTokenIssue[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [caretRect, setCaretRect] = useState<DOMRect | null>(null);
  const latestCanonicalRef = useRef(value);
  const latestCommitRef = useRef(onCommit);
  const variablesRef = useRef(variables);
  const pendingCleanupRef = useRef<(() => void) | null>(null);
  const hasPendingRef = useRef(false);
  const optionsRef = useRef({ allowVariables, checkVariables });

  useEffect(() => {
    latestCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    optionsRef.current = { allowVariables, checkVariables };
  }, [allowVariables, checkVariables]);

  const renderStoredValue = (storedValue: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    const rendered = richTextToEditorHtml(storedValue, variablesRef.current, optionsRef.current);
    if (editor.innerHTML !== rendered.html) {
      editor.innerHTML = rendered.html;
    }
    setIssues(rendered.issues);
  };

  useEffect(() => {
    if (isFocused) return;
    latestCanonicalRef.current = value;
    renderStoredValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, variables, allowVariables, checkVariables, isFocused]);

  const clearPendingFlush = () => {
    pendingCleanupRef.current?.();
    pendingCleanupRef.current = null;
    hasPendingRef.current = false;
  };

  const persistCanonicalValue = (nextValue: string) => {
    latestCommitRef.current(nextValue);
    clearPendingFlush();
  };

  const debouncedPersist = useDebouncedCallback((nextValue: string) => {
    persistCanonicalValue(nextValue);
  }, debounceMs);

  const serializeEditor = () => {
    const editor = editorRef.current;
    if (!editor) {
      return { value: latestCanonicalRef.current, issues: [] as VariableTokenIssue[] };
    }
    return editorDomToCanonical(editor, variablesRef.current, optionsRef.current);
  };

  const persistImmediateValue = () => {
    const result = serializeEditor();
    latestCanonicalRef.current = result.value;
    setIssues(result.issues);
    debouncedPersist.cancel?.();
    clearPendingFlush();
    persistCanonicalValue(result.value);
  };

  const flushPendingValue = () => {
    if (!hasPendingRef.current) {
      return;
    }

    debouncedPersist.flush?.();

    if (hasPendingRef.current) {
      persistCanonicalValue(latestCanonicalRef.current);
    }
  };

  const registerPendingFlush = () => {
    pendingCleanupRef.current?.();
    pendingCleanupRef.current = registerPendingTextEdit(flushPendingValue);
    hasPendingRef.current = true;
  };

  useEffect(() => {
    return () => {
      flushPendingValue();
      clearPendingFlush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPersist, ...flushOnCleanupKeys]);

  const warningText = useMemo(() => getWarningText(issues), [issues]);

  const getSelectionCaretRect = (): DOMRect | null => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.startContainer)) return null;

    // jsdom does not implement client rect APIs on Range.
    if (typeof range.getClientRects === "function") {
      const rects = range.getClientRects();
      if (rects.length > 0) return rects[0];
    }

    if (typeof range.getBoundingClientRect === "function") {
      const rect = range.getBoundingClientRect();
      if (rect && (rect.top !== 0 || rect.left !== 0 || rect.width !== 0 || rect.height !== 0)) {
        return rect;
      }
    }

    return editor.getBoundingClientRect();
  };

  const updateCaretRect = () => {
    setCaretRect(getSelectionCaretRect());
  };

  const {
    isSuggestionOpen,
    selectedSuggestionIndex,
    filteredSuggestions,
    suggestionMenuRef,
    closeSuggestionMenu,
    updateSuggestionState,
    insertVariable,
    handleKeyDown: handleIntellisenseKeyDown,
  } = useRichTextIntellisense({
    enabled: enableVariableIntellisense && allowVariables,
    variables,
    editorRef,
    onInserted: persistImmediateValue,
  });

  const handleInput: FormEventHandler<HTMLDivElement> = () => {
    const result = serializeEditor();
    latestCanonicalRef.current = result.value;
    setIssues(result.issues);
    registerPendingFlush();
    debouncedPersist(result.value);
    updateSuggestionState();
    updateCaretRect();
  };

  const handleFocus: FocusEventHandler<HTMLDivElement> = () => {
    setIsFocused(true);
    updateCaretRect();
  };

  const handleBlur: FocusEventHandler<HTMLDivElement> = () => {
    flushPendingValue();
    setIsFocused(false);
    setCaretRect(null);
    closeSuggestionMenu();
    // Normalize the DOM (convert typed [$Name] tokens to chips, canonical tag
    // structure) now that writing innerHTML cannot disturb the caret.
    renderStoredValue(latestCanonicalRef.current);
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = () => {
    updateSuggestionState();
    updateCaretRect();
  };

  const handleKeyUp: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "Enter" ||
      e.key === "Escape"
    ) {
      return;
    }

    updateSuggestionState();
    updateCaretRect();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    // Browsers apply Ctrl/Cmd+B/I/U natively in contentEditable; block the
    // shortcuts for formats that are currently disabled.
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      const shortcutCommand =
        key === "b" ? "bold" : key === "i" ? "italic" : key === "u" ? "underline" : null;
      if (shortcutCommand && !ENABLED_INLINE_FORMATS[shortcutCommand]) {
        e.preventDefault();
        return;
      }
    }

    const suggestionMenuWasOpen =
      isSuggestionOpen && filteredSuggestions.length > 0;
    handleIntellisenseKeyDown(e);

    if (e.key === "Enter" && !e.defaultPrevented) {
      if (onSubmitEnter && !e.shiftKey) {
        e.preventDefault();
        flushPendingValue();
        const shouldClear = onSubmitEnter(latestCanonicalRef.current);
        if (shouldClear && editorRef.current) {
          editorRef.current.innerHTML = "";
          latestCanonicalRef.current = "";
          setIssues([]);
        }
        return;
      }

      if (!suggestionMenuWasOpen) {
        e.preventDefault();
        document.execCommand?.("insertText", false, "\n");
      }
    }

    requestAnimationFrame(() => {
      updateCaretRect();
    });
  };

  const handlePaste: ClipboardEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand?.("insertText", false, text);
  };

  const caretAnchorEl: VirtualElement | null = useMemo(() => {
    if (!caretRect) return null;

    return {
      contextElement: editorRef.current ?? undefined,
      getBoundingClientRect: () => caretRect,
    };
  }, [caretRect]);

  return {
    editorRef,
    issues,
    warningText,
    isFocused,
    flushPendingValue,
    persistImmediateValue,
    handleInput,
    handleFocus,
    handleBlur,
    handleClick,
    handleKeyUp,
    handleKeyDown,
    handlePaste,
    suggestions: {
      open:
        enableVariableIntellisense &&
        isFocused &&
        isSuggestionOpen &&
        filteredSuggestions.length > 0,
      anchorEl: (caretAnchorEl ?? editorRef.current) as VirtualElement | HTMLElement | null,
      selectedIndex: selectedSuggestionIndex,
      filteredSuggestions,
      menuRef: suggestionMenuRef,
      onSelect: insertVariable,
    },
  };
}
